import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useQueryState, parseAsInteger, parseAsString } from "nuqs";
import { useCallback, useMemo } from "react";
import { schoolService, userService } from "../../../services";
import { transformStudentData } from "../../school-student/utils/transformStudentData";
import { useDebounce } from "../../../hooks/useDebounce";

// URL state parsers
const searchParser = parseAsString.withDefault("");
const statusParser = parseAsString.withDefault("");
const pageParser = parseAsInteger.withDefault(1);
const perPageParser = parseAsInteger.withDefault(10);

// Liên kết học sinh – lớp nằm ở bảng school_users (classId), không nằm trên user,
// nên phải lấy qua /schooluser/students thay vì /user/search.
const useStudent = ({ classId }: { classId: string }) => {
  const queryClient = useQueryClient();

  const [search, setSearch] = useQueryState("search", searchParser);
  const [status, setStatus] = useQueryState("status", statusParser);
  const [page, setPage] = useQueryState("page", pageParser);
  const [per_page, setPerPage] = useQueryState("per_page", perPageParser);

  // Ô tìm kiếm gắn thẳng với URL; chỉ giá trị gửi lên server được debounce để không gọi API mỗi phím
  const debouncedSearch = useDebounce(search, 500);

  const filters = useMemo(
    () => ({
      classId,
      search: debouncedSearch || undefined,
      status: status || undefined,
      page,
      per_page,
    }),
    [classId, debouncedSearch, status, page, per_page]
  );

  const queryKey = ["class-students", classId, filters];

  const {
    data: studentsData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await schoolService.getSchoolStudents(filters);
      const data = (response.data || []).map(transformStudentData);
      const pagination = response.pagination
        ? {
            page: response.pagination.current_page || 1,
            per_page: response.pagination.per_page || per_page,
            total: response.pagination.total || 0,
            totalPages: response.pagination.total_pages || 1,
          }
        : { page: 1, per_page: data.length, total: data.length, totalPages: 1 };
      return { data, pagination };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!classId,
    // Giữ trang cũ khi đổi tìm kiếm/trang để không bật lại skeleton, ô tìm kiếm không mất focus
    placeholderData: keepPreviousData,
  });

  // BE tự lấy trường từ lớp và ghi school_users kèm classId
  const createStudentMutation = useMutation({
    // Tạo tài khoản không gửi lại an toàn được (dễ tạo trùng), còn retry mặc định của app treo khi tab mất focus
    retry: false,
    mutationFn: (data: any) =>
      userService.createUser({
        name: data.name,
        email: data.email,
        phone: data.phone,
        user_name: data.user_name,
        role: "student",
        type: "student",
        classId,
        // Không gửi khối: khối lấy theo lớp
        metadata: {
          enrollmentDate: data.enrollmentDate,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["class-students", classId] });
      queryClient.invalidateQueries({ queryKey: ["school-students"] });
    },
  });

  const updateFilters = useCallback(
    (newFilters: Partial<Pick<typeof filters, "search" | "status">>) => {
      if (newFilters.search !== undefined) setSearch(newFilters.search || null);
      if (newFilters.status !== undefined) setStatus(newFilters.status || null);
      setPage(1);
    },
    [setSearch, setStatus, setPage]
  );

  const updatePagination = useCallback(
    (newPage: number, newPerPage?: number) => {
      setPage(newPage);
      if (newPerPage) setPerPage(newPerPage);
    },
    [setPage, setPerPage]
  );

  const resetFilters = useCallback(() => {
    setSearch(null);
    setStatus(null);
    setPage(1);
  }, [setSearch, setStatus, setPage]);

  return {
    students: studentsData?.data || [],
    pagination: studentsData?.pagination,

    isLoading,
    isFetching,
    isError,
    error,

    filters,
    searchInput: search,
    page,
    per_page,

    refetch,
    updateFilters,
    updatePagination,
    resetFilters,

    createStudent: createStudentMutation.mutate,
    isCreating: createStudentMutation.isPending,
    createStudentMutation,
  };
};

export default useStudent;
